// Story 5.6 — destructive AlertDialog confirming a listing withdrawal.
//
// Discriminated-union props on `kind` ("share" | "sealed_dossier") so the
// dialog renders the correct asset language + fires the correct mutation.
// Lighter shape than Story 5.5's BuyListingConfirmModal: cancel doesn't
// need the full listing card data (the parent surface already shows it),
// only the listing_id + kind.
//
// Per UX-DR L1754-L1757: destructive button (state-error-fill background)
// + always paired with confirm modal + NEVER takes immediate effect on
// click. Per UX-DR L1779: any irreversible action uses centered
// AlertDialog with title, body explaining consequence, action buttons in
// footer.
//
// Outcome handling:
//   * 200 → toast.success("Listing withdrawn") + close + invalidate
//     ["my-listings"] + ["vault-items"] + browse caches.
//   * 409 LISTING_NOT_ACTIVE → toast.error + close + invalidate
//     (a concurrent purchase / cancel just won).
//   * 404 LISTING_NOT_FOUND → toast.error + close + invalidate.
//   * other → toast.error(detail.message) + dialog stays open.

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  buttonVariants,
} from "@greenhold/components";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import { useCancelShareListing } from "./useCancelShareListing";
import { useCancelSealedDossierListing } from "./useCancelSealedDossierListing";

export interface CancelListingConfirmDialogProps {
  kind: "share" | "sealed_dossier";
  listingId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CancelListingConfirmDialog({
  kind,
  listingId,
  open,
  onOpenChange,
}: CancelListingConfirmDialogProps) {
  const queryClient = useQueryClient();
  const shareMutation = useCancelShareListing();
  const sealedDossierMutation = useCancelSealedDossierListing();
  const mutation = kind === "share" ? shareMutation : sealedDossierMutation;

  function invalidateAll() {
    void queryClient.invalidateQueries({ queryKey: ["my-listings"] });
    void queryClient.invalidateQueries({ queryKey: ["vault-items"] });
    void queryClient.invalidateQueries({
      queryKey: ["marketplace-share-listings"],
    });
    void queryClient.invalidateQueries({
      queryKey: ["marketplace-sealed-dossier-listings"],
    });
  }

  async function handleSubmit() {
    try {
      if (kind === "share") {
        await shareMutation.mutateAsync({ listing_id: listingId });
      } else {
        await sealedDossierMutation.mutateAsync({ listing_id: listingId });
      }
      toast.success("Listing withdrawn");
      onOpenChange(false);
      invalidateAll();
    } catch (err) {
      const detail = err as { code?: string; message?: string };
      if (detail?.code === "LISTING_NOT_ACTIVE") {
        toast.error("This listing was just sold or cancelled. Refreshing.");
        onOpenChange(false);
        invalidateAll();
        return;
      }
      if (detail?.code === "LISTING_NOT_FOUND") {
        toast.error("Listing not found.");
        onOpenChange(false);
        invalidateAll();
        return;
      }
      toast.error(detail?.message ?? "Withdrawal failed. Try again.");
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Withdraw this listing?</AlertDialogTitle>
          <AlertDialogDescription data-testid="cancel-dialog-description">
            {kind === "share"
              ? "Once withdrawn, this certificate listing is removed from the marketplace. Any open offers on it are declined and the bidders get their held money back. You can re-list it at any time."
              : "Once withdrawn, this sealed-dossier listing is removed from the marketplace. Any open offers on it are declined and the bidders get their held money back. You can re-list it at any time."}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={mutation.isPending}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            className={buttonVariants({ variant: "destructive" })}
            disabled={mutation.isPending}
            onClick={(e) => {
              // Keep the dialog open during the mutation so error states
              // can render in-dialog. The handleSubmit owns close timing.
              e.preventDefault();
              void handleSubmit();
            }}
            data-testid="confirm-withdraw-button"
          >
            {mutation.isPending ? "Withdrawing…" : "Withdraw listing"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
